import {
  Add,
  Dashboard as DashboardIcon,
  HelpOutline,
  Search,
  Settings,
} from '@mui/icons-material';
import { Button, IconButton, InputAdornment, TextField } from '@mui/material';
import { DashboardBriefDto } from '@thingslog/repositories';
import { cn } from '@thingslog/ui-components';
import React, { FC, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import useDetermineCompanyId from '../../../../hooks/useDetermineCompanyId';
import { useDashboardSelection } from '../../hooks';
import { DashboardSelectionList } from './DashboardSelectionList';

interface Props {
  open: boolean;
  dashboards: DashboardBriefDto[];
  currentDashboardId?: string;
  onAddDashboard: () => void;
  onEditCurrentDashboard?: () => void;
  onSelectDashboard: (dashboardId: string) => void;
  onStartTour: () => void;
  className?: string;
}

export const DashboardSelectionSidebar: FC<Props> = ({
  open,
  dashboards,
  currentDashboardId,
  onAddDashboard,
  onEditCurrentDashboard,
  onSelectDashboard,
  onStartTour,
  className,
}: Props) => {
  const { t } = useTranslation();
  const companyId = useDetermineCompanyId();
  const [searchTerm, setSearchTerm] = useState('');
  const { handleNavigateToDashboards, handleSetDefaultDashboard, handleToggleFavoriteDashboard } =
    useDashboardSelection(companyId);

  const filteredDashboards = useMemo((): DashboardBriefDto[] => {
    const normalizedSearchTerm = searchTerm.trim().toLowerCase();

    if (!normalizedSearchTerm) {
      return dashboards;
    }

    return dashboards.filter((dashboard: DashboardBriefDto): boolean =>
      dashboard.name.toLowerCase().includes(normalizedSearchTerm)
    );
  }, [dashboards, searchTerm]);

  return (
    <aside
      className={cn(
        className,
        open
          ? 'fixed inset-x-0 bottom-16 top-0 z-40 flex min-w-0 flex-col gap-3 overflow-hidden border-0 border-r border-solid border-gray-200 bg-white p-3 md:relative md:inset-auto md:bottom-auto md:top-auto md:z-auto md:h-full md:w-80 md:min-w-[20rem]'
          : 'hidden'
      )}
    >
      <div data-dashboard-tour="dashboard-selection-search">
        <TextField
          value={searchTerm}
          onChange={(event: React.ChangeEvent<HTMLInputElement>): void =>
            setSearchTerm(event.target.value)
          }
          label={t('dashboard_search_dashboards', 'Search dashboards')}
          placeholder={t('dashboard_search_by_name', 'Search by name')}
          size="small"
          fullWidth
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search fontSize="small" />
              </InputAdornment>
            ),
          }}
        />
      </div>

      <div className="flex flex-col gap-2">
        <Button
          variant="contained"
          color="primary"
          disableElevation
          startIcon={<Add />}
          data-dashboard-tour="dashboard-selection-add"
          onClick={onAddDashboard}
        >
          {t('dashboard_add_dashboard', 'Add Dashboard')}
        </Button>
        {onEditCurrentDashboard && (
          <Button
            variant="outlined"
            color="primary"
            disableElevation
            startIcon={<Settings />}
            onClick={onEditCurrentDashboard}
          >
            {t('dashboard_edit_current_dashboard', 'Edit Current Dashboard')}
          </Button>
        )}
      </div>

      <div
        className="min-h-0 flex-1 overflow-y-auto"
        data-dashboard-tour="dashboard-selection-list"
      >
        <DashboardSelectionList
          dashboards={filteredDashboards}
          currentDashboardId={currentDashboardId}
          onSelectDashboard={onSelectDashboard}
          onSetDefaultDashboard={handleSetDefaultDashboard}
          onToggleFavoriteDashboard={handleToggleFavoriteDashboard}
        />
      </div>

      <div className="flex shrink-0 items-center gap-2">
        <Button
          variant="outlined"
          color="primary"
          disableElevation
          fullWidth
          startIcon={<DashboardIcon />}
          data-dashboard-tour="dashboard-selection-all"
          onClick={handleNavigateToDashboards}
        >
          {t('dashboard_all_dashboards', 'All Dashboards')}
        </Button>
        <IconButton
          size="small"
          aria-label={t('dashboard_start_selector_guide', 'Start dashboard selector guide')}
          data-dashboard-tour="dashboard-selection-help"
          onClick={onStartTour}
        >
          <HelpOutline fontSize="small" />
        </IconButton>
      </div>
    </aside>
  );
};
